import { useRef, useState } from 'react';

/**
 * Shared Save / Load UI for Economics and Forecast libraries (localStorage, not website DB).
 */
export default function LibraryPanel({
  kindLabel,
  entries,
  activeMeta,
  message,
  onDismissMessage,
  onSaveAs,
  onOverwrite,
  onLoad,
  onDelete,
  onDownloadLibrary,
  onUploadLibrary,
}) {
  const [name, setName] = useState(activeMeta?.libraryName || '');
  const [notes, setNotes] = useState('');
  const fileRef = useRef(null);

  return (
    <section className="chart-card economics-panel">
      <h2>Saved {kindLabel}</h2>
      <p className="chart-hint">
        Named copies live in this browser only (localStorage) — not the product database. Export the
        library JSON for backup or to share with Cursor MCP (<code>~/.ffc-ops/scenarios</code>).
      </p>

      {activeMeta?.libraryName && (
        <p className="module-status economics-callout">
          Working copy linked to: <strong>{activeMeta.libraryName}</strong>
        </p>
      )}

      {message && (
        <p className="module-status economics-callout" role="status">
          {message}
          <button
            type="button"
            className="secondary-button forecast-dismiss-btn"
            onClick={onDismissMessage}
          >
            Dismiss
          </button>
        </p>
      )}

      <h3 className="economics-subheading">Save current</h3>
      <div className="economics-form-grid economics-form-grid-3">
        <label className="economics-field">
          <span>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Pre-launch PAYG stress"
          />
        </label>
        <label className="economics-field">
          <span>Notes</span>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
          />
        </label>
      </div>
      <div className="economics-preset-row">
        <button
          type="button"
          className="primary-button"
          onClick={() => {
            const entry = onSaveAs({ name, notes });
            if (entry) setName(entry.name);
          }}
        >
          Save as new
        </button>
        {activeMeta?.libraryId && (
          <button
            type="button"
            className="secondary-button"
            onClick={() =>
              onOverwrite({
                id: activeMeta.libraryId,
                name: name || activeMeta.libraryName,
                notes,
              })
            }
          >
            Overwrite “{activeMeta.libraryName}”
          </button>
        )}
      </div>

      <h3 className="economics-subheading">Library</h3>
      {entries.length === 0 ? (
        <p className="economics-muted">No saved scenarios yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Updated</th>
                <th>Notes</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className={
                    entry.id === activeMeta?.libraryId ? 'forecast-row-active' : undefined
                  }
                >
                  <td>{entry.name}</td>
                  <td className="economics-muted">
                    {entry.updatedAt ? new Date(entry.updatedAt).toLocaleString() : '—'}
                  </td>
                  <td className="economics-muted">{entry.notes || '—'}</td>
                  <td>
                    <div className="economics-preset-row">
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => {
                          onLoad(entry.id);
                          setName(entry.name);
                          setNotes(entry.notes || '');
                        }}
                      >
                        Load
                      </button>
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => {
                          if (
                            window.confirm(`Delete “${entry.name}”? This cannot be undone.`)
                          ) {
                            onDelete(entry.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="economics-subheading">Backup</h3>
      <div className="economics-preset-row">
        <button type="button" className="secondary-button" onClick={onDownloadLibrary}>
          Export library JSON
        </button>
        <button
          type="button"
          className="secondary-button"
          onClick={() => fileRef.current?.click()}
        >
          Import library JSON
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUploadLibrary(file, 'merge');
            e.target.value = '';
          }}
        />
      </div>
    </section>
  );
}
